import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedName, GeneratedName, NameGeneratorInput } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface NameStore {
  savedNames: SavedName[]
  maxNames: number
  
  // アクション
  saveName: (name: GeneratedName, input: NameGeneratorInput) => string | null
  deleteName: (id: string) => void
  getName: (id: string) => SavedName | undefined
  updateNameMemo: (id: string, memo: string) => void
  updateNameTags: (id: string, tags: string[]) => void
  exportAsText: () => void
  exportAsJSON: () => void
  importFromJSON: (jsonString: string) => void
  clearAllNames: () => void
  isNameSaved: (name: string) => boolean
}

export const useNameStore = create<NameStore>()(
  persist(
    (set, get) => ({
      savedNames: [],
      maxNames: 1000,

      saveName: (generatedName: GeneratedName, input: NameGeneratorInput) => {
        const { savedNames, maxNames } = get()
        
        // 既に保存されているかチェック
        const existing = savedNames.find(n => n.name === generatedName.name)
        if (existing) {
          return existing.id
        }
        
        // 最大保存数チェック
        if (savedNames.length >= maxNames) {
          alert(`保存数が上限（${maxNames}件）に達しています。不要な名前を削除してください。`)
          return null
        }

        const newName: SavedName = {
          id: crypto.randomUUID(),
          name: generatedName.name,
          reading: generatedName.reading,
          meaning: generatedName.meaning,
          nameType: input.nameType,
          subcategory: input.subcategory,
          style: input.style,
          rarity: generatedName.rarity || 5,
          createdAt: new Date().toISOString(),
        }

        set((state) => ({
          savedNames: [...state.savedNames, newName],
        }))

        return newName.id
      },

      deleteName: (id: string) => {
        set((state) => ({
          savedNames: state.savedNames.filter((name) => name.id !== id),
        }))
      },

      getName: (id: string) => {
        return get().savedNames.find((name) => name.id === id)
      },

      updateNameMemo: (id: string, memo: string) => {
        set((state) => ({
          savedNames: state.savedNames.map((name) =>
            name.id === id
              ? { ...name, memo }
              : name
          ),
        }))
      },

      updateNameTags: (id: string, tags: string[]) => {
        set((state) => ({
          savedNames: state.savedNames.map((name) =>
            name.id === id
              ? { ...name, tags }
              : name
          ),
        }))
      },

      exportAsText: () => {
        const { savedNames } = get()
        if (savedNames.length === 0) {
          alert('保存された名前がありません。')
          return
        }

        const content = formatNamesForExport(savedNames)
        downloadTextFile(content, `saved_names_${Date.now()}.txt`)
      },

      exportAsJSON: () => {
        const { savedNames } = get()
        if (savedNames.length === 0) {
          alert('保存された名前がありません。')
          return
        }

        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'names',
          data: savedNames
        }

        downloadJSONFile(exportData, `saved_names_${Date.now()}.json`)
      },

      importFromJSON: (jsonString: string) => {
        try {
          const importData = JSON.parse(jsonString)
          
          // バリデーション
          if (!importData.data || !Array.isArray(importData.data)) {
            throw new Error('無効なデータ形式です')
          }

          const { maxNames, savedNames } = get()
          const newNames = importData.data as SavedName[]
          
          // 現在の名前と重複チェック（名前と読みがらで判定）
          const uniqueNames = newNames.filter(newName => {
            return !savedNames.some(existing => 
              existing.name === newName.name && 
              existing.reading === newName.reading
            )
          })
          
          // マージした結果が最大数を超える場合
          const totalCount = savedNames.length + uniqueNames.length
          if (totalCount > maxNames) {
            const availableSlots = maxNames - savedNames.length
            if (availableSlots <= 0) {
              alert(`保存数が上限（${maxNames}件）に達しています。不要な名前を削除してください。`)
              return
            }
            if (!confirm(`追加可能な件数は${availableSlots}件です。${uniqueNames.length}件中${availableSlots}件のみインポートしますか？`)) {
              return
            }
            uniqueNames.splice(availableSlots)
          }

          // IDの重複を避けるため、新しいIDを生成して追加
          const importedNames = uniqueNames.map(name => ({
            ...name,
            id: crypto.randomUUID(),
            createdAt: name.createdAt || new Date().toISOString()
          }))
          
          const mergedNames = [...savedNames, ...importedNames]

          set({ savedNames: mergedNames })
          
          const skippedCount = newNames.length - uniqueNames.length
          alert(`${importedNames.length}件の名前をインポートしました。${skippedCount > 0 ? `(${skippedCount}件の重複をスキップ)` : ''}`)
        } catch (error) {
          console.error('Import error:', error)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
        }
      },

      clearAllNames: () => {
        if (confirm('保存されているすべての名前を削除しますか？')) {
          set({ savedNames: [] })
        }
      },

      isNameSaved: (name: string) => {
        return get().savedNames.some(n => n.name === name)
      },
    }),
    {
      name: 'name-store',
      version: 1,
      storage: createIndexedDBStorage('name-store'),
      partialize: (state) => ({ savedNames: state.savedNames, maxNames: state.maxNames }),
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return persistedState
        }
        return persistedState
      }
    }
  )
)

// 名前をテキスト形式にフォーマット
function formatNamesForExport(names: SavedName[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  // 名前タイプ別にグループ化
  const grouped = names.reduce((acc, name) => {
    if (!acc[name.nameType]) {
      acc[name.nameType] = []
    }
    acc[name.nameType].push(name)
    return acc
  }, {} as Record<string, SavedName[]>)

  let content = `【保存された名前一覧】
生成日時: ${date}
総数: ${names.length}件

`

  // タイプ別に出力
  const typeLabels: Record<string, string> = {
    person_full: '人名（フルネーム）',
    person_family: '人名（名字）',
    person_given: '人名（名前）',
    weapon: '武器名',
    skill: 'スキル名',
    place: '地名',
    organization: '組織名',
    item: 'アイテム名',
    magic: '魔法名',
    title: '称号',
    enemy: 'エネミー名',
  }

  for (const [type, typeNames] of Object.entries(grouped)) {
    content += `\n【${typeLabels[type] || type}】 (${typeNames.length}件)\n`
    content += '=' .repeat(50) + '\n\n'

    typeNames.forEach((name) => {
      content += `${name.name}`
      if (name.reading) {
        content += ` (${name.reading})`
      }
      content += '\n'
      
      if (name.meaning) {
        content += `  意味: ${name.meaning}\n`
      }
      
      content += `  スタイル: ${name.style} | 珍しさ: ${name.rarity}/10`
      
      if (name.subcategory) {
        content += ` | カテゴリ: ${name.subcategory}`
      }
      
      if (name.memo) {
        content += `\n  メモ: ${name.memo}`
      }
      
      if (name.tags && name.tags.length > 0) {
        content += `\n  タグ: ${name.tags.join(', ')}`
      }
      
      content += '\n\n'
    })
  }

  return content
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// JSONファイルをダウンロード
function downloadJSONFile(data: any, filename: string) {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}